import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get, post, put, del } from '../../api/base';
import {
  ActionType,
  DeviationType,
  MatrixLikelihoodType,
  MatrixRiskType,
  MatrixSeverityType,
  MatrixType,
  UpdateCauseType,
} from '../../types/types';

interface DeviationState {
  deviation: DeviationType;
  deviations: DeviationType[];
  loading: boolean;
  error: any;
  safeguardNamesByProject: Record<number, string[]>;
  loadingSafeguardNames: Record<number, boolean>;
  projectActionsByProject: Record<number, ActionType[]>;
  loadingProjectActions: Record<number, boolean>;
  projectActionsLoaded: Record<number, boolean>;
  safeguardNamesLoaded: Record<number, boolean>;
}

export const initialState: DeviationState = {
  deviation: {
    id: null,
    name: '',
    node: null,
    causes: [],
    consequences: [],
    safeguards: [],
    actions: [],
    created_by: null,
    created_at: '',
    updated_by: null,
    updated_at: '',
    color: '',
  },
  deviations: [],
  loading: false,
  error: null,
  safeguardNamesByProject: {},
  loadingSafeguardNames: {},
  projectActionsByProject: {},
  loadingProjectActions: {},
  projectActionsLoaded: {},
  safeguardNamesLoaded: {},
};

export const getAllDeviationsByNodeId = createAsyncThunk(
  'deviations/getAllDeviationsByNodeId',
  async (nodeId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await get(`node/${nodeId}/deviations/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getDeviation = createAsyncThunk(
  'deviations/getDeviation',
  async (deviation_id: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await get(`deviation/${deviation_id}/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export type CreateDeviationType = {
  name: string;
  nodeId: number;
  color?: string;
};

export const createDeviation = createAsyncThunk(
  'deviations/createDeviation',
  async (createDeviation: CreateDeviationType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await post(`node/${createDeviation.nodeId}/deviations/`, createDeviation, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const cloneDeviation = createAsyncThunk(
  'deviations/cloneDeviation',
  async (deviationId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await post(`deviation/${deviationId}/clone/`, {}, { headers });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateDeviation = createAsyncThunk(
  'deviations/updateDeviation',
  async (deviation: DeviationType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await put(`deviation/${deviation.id}/`, deviation, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteDeviation = createAsyncThunk(
  'deviations/deleteDeviation',
  async (deviation: DeviationType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      await del(`deviation/${deviation.id}/`, {
        headers: headers,
      });
      return deviation;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

// deviationsSlice thunks – mover arriba/abajo
export const moveDeviationUp = createAsyncThunk(
  'deviations/moveDeviationUp',
  async (deviationId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = { Authorization: `Bearer ${state.user.access}` };
      const response = await post(`deviation/${deviationId}/up/`, {}, { headers });
      // backend responde { id, order }
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data ?? error);
    }
  }
);

export const moveDeviationDown = createAsyncThunk(
  'deviations/moveDeviationDown',
  async (deviationId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = { Authorization: `Bearer ${state.user.access}` };
      const response = await post(`deviation/${deviationId}/down/`, {}, { headers });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data ?? error);
    }
  }
);

export type CreateCauseType = {
  deviation: number;
  name: string;
};

export const createCause = createAsyncThunk(
  'deviations/createCause',
  async (newCause: CreateCauseType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await post(`deviation/${newCause.deviation}/causes/`, newCause, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const cloneCause = createAsyncThunk(
  'deviations/cloneCause',
  async (causeId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await post(`causes/${causeId}/clone/`, {}, { headers });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export type CreateConsequenceType = {
  deviation: number;
  name: string;
  causes?: Partial<{ id: number; name: string }>[];
};

export const createConsequence = createAsyncThunk(
  'deviations/createConsequence',
  async (newConsequence: CreateConsequenceType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const primaryCauseId = newConsequence.causes?.[0]?.id;
      const endpoint = primaryCauseId
        ? `cause/${primaryCauseId}/consequences/`
        : `deviation/${newConsequence.deviation}/consequences/`;
      const response = await post(endpoint, newConsequence, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const cloneConsequence = createAsyncThunk(
  'deviations/cloneConsequence',
  async (consequenceId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await post(`consequences/${consequenceId}/clone/`, {}, { headers });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export type CreateSafeguardType = {
  deviation: number;
  name: string;
  consequences?: Partial<{ id: number; name: string }>[];
};

export const createSafeguard = createAsyncThunk(
  'deviations/createSafeguard',
  async (newSafeguard: CreateSafeguardType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const primaryConsequenceId = newSafeguard.consequences?.[0]?.id;
      const endpoint = primaryConsequenceId
        ? `consequence/${primaryConsequenceId}/safeguards/`
        : `deviation/${newSafeguard.deviation}/safeguards/`;
      const response = await post(endpoint, newSafeguard, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export type CreateActionType = {
  deviation: number;
  name: string;
  responsability: string;
  asociated_risk: string;
  consequences?: Array<number | Partial<{ id: number; name: string }>>;
  project_order?: number | null;
};

export const createAction = createAsyncThunk(
  'deviations/createAction',
  async (newAction: CreateActionType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const formattedConsequences =
        newAction.consequences
          ?.map((item) => {
            if (typeof item === 'number') return item;
            if (item && typeof item === 'object' && 'id' in item) {
              return item.id ?? null;
            }
            return null;
          })
          .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id)) ?? [];
      const primaryConsequenceId = formattedConsequences[0];
      const payload = {
        ...newAction,
        consequences: formattedConsequences,
      };
      const endpoint = primaryConsequenceId
        ? `consequence/${primaryConsequenceId}/actions/`
        : `deviation/${newAction.deviation}/actions/`;
      const response = await post(endpoint, payload, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getProjectActionsByProjectId = createAsyncThunk(
  'deviations/getProjectActionsByProjectId',
  async (projectId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await get(`project/${projectId}/actions/full/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data ?? error);
    }
  }
);

export const getSafeguardNamesByProjectId = createAsyncThunk(
  'deviations/getSafeguardNamesByProjectId',
  async (projectId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await get(`project/${projectId}/safeguards/names/`, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data ?? error);
    }
  }
);

export const assignActionToConsequence = createAsyncThunk(
  'deviations/assignActionToConsequence',
  async (
    payload: { actionId: number; consequenceId: number; deviationId?: number | null; consequenceIds?: number[] },
    { rejectWithValue, getState }
  ) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const body: Record<string, any> = {
        consequences: (payload.consequenceIds ?? [payload.consequenceId]).map((id) => ({ id })),
      };
      if (payload.deviationId) {
        body.deviation = payload.deviationId;
      }
      const response = await post(`actions/${payload.actionId}/consequences/`, body, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data ?? error);
    }
  }
);

export const deleteCause = createAsyncThunk(
  'deviations/deleteCause',
  async (causeId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      await del(`causes/${causeId}/`, {
        headers: headers,
      });
      return causeId;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteConsequence = createAsyncThunk(
  'deviations/deleteConsequence',
  async (consequenceId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      await del(`consequences/${consequenceId}/`, {
        headers: headers,
      });
      return consequenceId;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteSafeguard = createAsyncThunk(
  'deviations/deleteSafeguard',
  async (safeguardId: number, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      await del(`safeguards/${safeguardId}/`, {
        headers: headers,
      });
      return safeguardId;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteAction = createAsyncThunk(
  'deviations/deleteAction',
  async (
    args: { actionId: number; consequenceId?: number | null },
    { rejectWithValue, getState }
  ) => {
    try {
      const { actionId, consequenceId = null } = args;
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const query = consequenceId ? `?consequence=${consequenceId}` : '';
      await del(`actions/${actionId}/${query}`, {
        headers: headers,
      });
      return { actionId, consequenceId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data ?? error);
    }
  }
);

export const updateCause = createAsyncThunk(
  'deviations/updateCause',
  async (cause: UpdateCauseType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await put(`causes/${cause.id}/`, cause, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export type UpdateConsequenceType = {
  id: number;
  name?: string;
  potential_risk_matrix?: MatrixType | number | null;
  potential_risk_matrix_severity?: MatrixSeverityType | number | null;
  potential_risk_matrix_likelihood?: MatrixLikelihoodType | number | null;
  potential_risk_matrix_risk?: MatrixRiskType | number | null;
  mitigated_risk_matrix?: MatrixType | number | null;
  mitigated_risk_matrix_severity?: MatrixSeverityType | number | null;
  mitigated_risk_matrix_likelihood?: MatrixLikelihoodType | number | null;
  mitigated_risk_matrix_risk?: MatrixRiskType | number | null;
  projected_risk_matrix?: MatrixType | number | null;
  projected_risk_matrix_severity?: MatrixSeverityType | number | null;
  projected_risk_matrix_likelihood?: MatrixLikelihoodType | number | null;
  projected_risk_matrix_risk?: MatrixRiskType | number | null;
};

export const updateConsequence = createAsyncThunk(
  'deviations/updateConsequence',
  async (consequence: UpdateConsequenceType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await put(`consequences/${consequence.id}/`, consequence, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export type UpdateSafeguardType = {
  id: number;
  name: string;
};

export const updateSafeguard = createAsyncThunk(
  'deviations/updateSafeguard',
  async (safeguard: UpdateSafeguardType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const response = await put(`safeguards/${safeguard.id}/`, safeguard, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

export type UpdateActionType = {
  id: number;
  name: string;
  responsability: string;
  asociated_risk: string;
  project_order?: number | null;
  consequences?: Array<number | { id?: number | null }>;
};

export const updateAction = createAsyncThunk(
  'deviations/updateAction',
  async (action: UpdateActionType, { rejectWithValue, getState }) => {
    try {
      const state: any = getState();
      const headers = {
        Authorization: `Bearer ${state.user.access}`,
      };
      const formattedConsequences =
        action.consequences
          ?.map((item) => {
            if (typeof item === 'number') return item;
            if (item && typeof item === 'object' && 'id' in item) {
              return item.id ?? null;
            }
            return null;
          })
          .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id)) ?? [];
      const payload = {
        ...action,
        consequences: formattedConsequences,
      };
      const response = await put(`actions/${action.id}/`, payload, {
        headers: headers,
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    }
  }
);

const deviationSlice = createSlice({
  name: 'deviations',
  initialState,
  reducers: {
    setDeviation: (state, action) => {
      state.deviation = action.payload;
    },
    clearDeviation: (state) => {
      state.deviation = initialState.deviation;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllDeviationsByNodeId.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllDeviationsByNodeId.fulfilled, (state, action) => {
        state.loading = false;
        state.deviations = action.payload;
      })
      .addCase(getAllDeviationsByNodeId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getDeviation.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDeviation.fulfilled, (state, action) => {
        state.loading = false;
        state.deviation = action.payload;
      })
      .addCase(getDeviation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createDeviation.pending, (state) => {
        state.loading = true;
      })
      .addCase(createDeviation.fulfilled, (state, action) => {
        state.loading = false;
        state.deviations.push(action.payload);
      })
      .addCase(createDeviation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateDeviation.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDeviation.fulfilled, (state, action) => {
        state.loading = false;
        state.deviation = action.payload;
      })
      .addCase(updateDeviation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteDeviation.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDeviation.fulfilled, (state, action) => {
        state.loading = false;
        state.deviations = state.deviations.filter((deviation) => deviation.id !== action.payload.id);
      })
      .addCase(deleteDeviation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(cloneDeviation.pending, (state) => {
        state.loading = true;
      })
      .addCase(cloneDeviation.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(cloneDeviation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getProjectActionsByProjectId.pending, (state, action) => {
        const projectId = action.meta.arg;
        if (typeof projectId === 'number') {
          state.loadingProjectActions[projectId] = true;
          state.projectActionsLoaded[projectId] = false;
        }
      })
      .addCase(getProjectActionsByProjectId.fulfilled, (state, action) => {
        const projectId = action.meta.arg;
        if (typeof projectId === 'number') {
          state.loadingProjectActions[projectId] = false;
          state.projectActionsLoaded[projectId] = true;
          const actions = Array.isArray(action.payload) ? action.payload : [];
          const sorted = [...actions].sort((a: ActionType, b: ActionType) => {
            const orderA = a.project_order ?? Number.MAX_SAFE_INTEGER;
            const orderB = b.project_order ?? Number.MAX_SAFE_INTEGER;
            if (orderA !== orderB) {
              return orderA - orderB;
            }
            const idA = typeof a.id === 'number' ? a.id : Number.MAX_SAFE_INTEGER;
            const idB = typeof b.id === 'number' ? b.id : Number.MAX_SAFE_INTEGER;
            return idA - idB;
          });
          state.projectActionsByProject[projectId] = sorted;
        }
      })
      .addCase(getProjectActionsByProjectId.rejected, (state, action) => {
        const projectId = action.meta.arg;
        if (typeof projectId === 'number') {
          state.loadingProjectActions[projectId] = false;
          state.projectActionsLoaded[projectId] = false;
        }
        state.error = action.payload;
      })
      .addCase(getSafeguardNamesByProjectId.pending, (state, action) => {
        const projectId = action.meta.arg;
        if (typeof projectId === 'number') {
          state.loadingSafeguardNames[projectId] = true;
          state.safeguardNamesLoaded[projectId] = false;
        }
      })
      .addCase(getSafeguardNamesByProjectId.fulfilled, (state, action) => {
        const projectId = action.meta.arg;
        if (typeof projectId === 'number') {
          state.loadingSafeguardNames[projectId] = false;
          state.safeguardNamesByProject[projectId] = action.payload ?? [];
          state.safeguardNamesLoaded[projectId] = true;
        }
      })
      .addCase(getSafeguardNamesByProjectId.rejected, (state, action) => {
        const projectId = action.meta.arg;
        if (typeof projectId === 'number') {
          state.loadingSafeguardNames[projectId] = false;
          state.safeguardNamesLoaded[projectId] = false;
        }
        state.error = action.payload;
      })
      .addCase(createCause.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCause.fulfilled, (state, action) => {
        state.loading = false;
        if (!state.deviation) return;
        const newCause = {
          ...action.payload,
          consequences: action.payload.consequences ?? [],
        };
        state.deviation.causes = [...(state.deviation.causes ?? []), newCause];
      })
      .addCase(createCause.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(cloneCause.pending, (state) => {
        state.loading = true;
      })
      .addCase(cloneCause.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(cloneCause.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createConsequence.pending, (state) => {
        state.loading = true;
      })
      .addCase(createConsequence.fulfilled, (state, action) => {
        state.loading = false;
        const consequence = action.payload;
        if (state.deviation) {
          const existingConsequences = state.deviation.consequences ?? [];
          const hasConsequence = existingConsequences.some((item) => item.id === consequence.id);
          state.deviation.consequences = hasConsequence
            ? existingConsequences.map((item) => (item.id === consequence.id ? { ...item, ...consequence } : item))
            : [...existingConsequences, consequence];
          if (state.deviation.causes) {
            const causeIds =
              action.meta?.arg?.causes?.map((causeRef: Partial<{ id: number }>) => causeRef.id).filter(Boolean) ?? [];
            state.deviation.causes = state.deviation.causes.map((cause) => {
              if (!cause.id) return cause;
              const shouldAttach =
                cause.consequences?.some((item) => item.id === consequence.id) || causeIds.includes(cause.id);
              if (!shouldAttach) return cause;
              const existing = cause.consequences ?? [];
              const exists = existing.some((item) => item.id === consequence.id);
              const nextConsequences = exists
                ? existing.map((item) => (item.id === consequence.id ? { ...item, ...consequence } : item))
                : [...existing, consequence];
              return { ...cause, consequences: nextConsequences };
            });
          }
        }
      })
      .addCase(createConsequence.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(cloneConsequence.pending, (state) => {
        state.loading = true;
      })
      .addCase(cloneConsequence.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(cloneConsequence.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createSafeguard.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSafeguard.fulfilled, (state, action) => {
        state.loading = false;
        const safeguard = action.payload;
        if (state.deviation) {
          const projectId = state.deviation.project;
          if (typeof projectId === 'number') {
            const existingNames = state.safeguardNamesByProject[projectId] ?? [];
            if (safeguard?.name && !existingNames.includes(safeguard.name)) {
              state.safeguardNamesByProject[projectId] = [...existingNames, safeguard.name];
            }
          }
          const existingSafeguards = state.deviation.safeguards ?? [];
          const hasSafeguard = existingSafeguards.some((item) => item.id === safeguard.id);
          state.deviation.safeguards = hasSafeguard
            ? existingSafeguards.map((item) => (item.id === safeguard.id ? { ...item, ...safeguard } : item))
            : [...existingSafeguards, safeguard];
          const consequenceArg = action.meta?.arg?.consequences ?? [];
          const consequenceIds = Array.isArray(consequenceArg)
            ? consequenceArg
                .map((consequenceRef: any) => {
                  if (typeof consequenceRef === 'number') {
                    return consequenceRef;
                  }
                  if (consequenceRef && typeof consequenceRef === 'object' && 'id' in consequenceRef) {
                    return consequenceRef.id ?? null;
                  }
                  return null;
                })
                .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id))
            : [];
          if (state.deviation.consequences) {
            state.deviation.consequences = state.deviation.consequences.map((consequence) => {
              if (!consequence.id || !consequenceIds.includes(consequence.id)) return consequence;
              const existing = consequence.safeguards ?? [];
              const exists = existing.some((item) => item.id === safeguard.id);
              const nextSafeguards = exists
                ? existing.map((item) => (item.id === safeguard.id ? { ...item, ...safeguard } : item))
                : [...existing, safeguard];
              return { ...consequence, safeguards: nextSafeguards };
            });
          }
          if (state.deviation.causes) {
            state.deviation.causes = state.deviation.causes.map((cause) => {
              if (!cause.consequences) return cause;
              const updatedConsequences = cause.consequences.map((consequence) => {
                if (!consequence.id || !consequenceIds.includes(consequence.id)) return consequence;
                const existing = consequence.safeguards ?? [];
                const exists = existing.some((item) => item.id === safeguard.id);
                const nextSafeguards = exists
                  ? existing.map((item) => (item.id === safeguard.id ? { ...item, ...safeguard } : item))
                  : [...existing, safeguard];
                return { ...consequence, safeguards: nextSafeguards };
              });
              return { ...cause, consequences: updatedConsequences };
            });
          }
        }
      })
      .addCase(createSafeguard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(createAction.fulfilled, (state, action) => {
        state.loading = false;
        const actionItem = action.payload;
        if (state.deviation) {
          const projectId = state.deviation.project;
          if (typeof projectId === 'number') {
            const existingProjectActions = state.projectActionsByProject[projectId] ?? [];
            const deduped = existingProjectActions.filter((item) => item.id !== actionItem.id);
            const nextProjectActions = [...deduped, actionItem].sort((a: ActionType, b: ActionType) => {
              const orderA = a.project_order ?? Number.MAX_SAFE_INTEGER;
              const orderB = b.project_order ?? Number.MAX_SAFE_INTEGER;
              if (orderA !== orderB) {
                return orderA - orderB;
              }
              const idA = typeof a.id === 'number' ? a.id : Number.MAX_SAFE_INTEGER;
              const idB = typeof b.id === 'number' ? b.id : Number.MAX_SAFE_INTEGER;
              return idA - idB;
            });
            state.projectActionsByProject[projectId] = nextProjectActions;
            state.projectActionsLoaded[projectId] = true;
          }
          const existingActions = state.deviation.actions ?? [];
          const hasAction = existingActions.some((item) => item.id === actionItem.id);
          state.deviation.actions = hasAction
            ? existingActions.map((item) => (item.id === actionItem.id ? { ...item, ...actionItem } : item))
            : [...existingActions, actionItem];
          const consequenceArg = action.meta?.arg?.consequences ?? [];
          const consequenceIds = Array.isArray(consequenceArg)
            ? consequenceArg
                .map((consequenceRef: any) => {
                  if (typeof consequenceRef === 'number') {
                    return consequenceRef;
                  }
                  if (consequenceRef && typeof consequenceRef === 'object' && 'id' in consequenceRef) {
                    return consequenceRef.id ?? null;
                  }
                  return null;
                })
                .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id))
            : [];
          if (state.deviation.consequences) {
            state.deviation.consequences = state.deviation.consequences.map((consequence) => {
              if (!consequence.id || !consequenceIds.includes(consequence.id)) return consequence;
              const existing = consequence.actions ?? [];
              const exists = existing.some((item) => item.id === actionItem.id);
              const nextActions = exists
                ? existing.map((item) => (item.id === actionItem.id ? { ...item, ...actionItem } : item))
                : [...existing, actionItem];
              return { ...consequence, actions: nextActions };
            });
          }
          if (state.deviation.causes) {
            state.deviation.causes = state.deviation.causes.map((cause) => {
              if (!cause.consequences) return cause;
              const updatedConsequences = cause.consequences.map((consequence) => {
                if (!consequence.id || !consequenceIds.includes(consequence.id)) return consequence;
                const existing = consequence.actions ?? [];
                const exists = existing.some((item) => item.id === actionItem.id);
                const nextActions = exists
                  ? existing.map((item) => (item.id === actionItem.id ? { ...item, ...actionItem } : item))
                  : [...existing, actionItem];
                return { ...consequence, actions: nextActions };
              });
              return { ...cause, consequences: updatedConsequences };
            });
          }
        }
      })
      .addCase(createAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(assignActionToConsequence.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignActionToConsequence.fulfilled, (state, action) => {
        state.loading = false;
        const consequenceId = action.meta?.arg?.consequenceId;
        const actionItemPayload = action.payload as ActionType & {
          consequences?: Array<number | { id?: number | null } | null> | null;
        };
        const consequenceIds = Array.isArray(actionItemPayload?.consequences)
          ? actionItemPayload.consequences
              .map((item) => {
                if (typeof item === 'number') return item;
                if (item && typeof item === 'object' && 'id' in item) {
                  return (item as { id?: number | null }).id ?? null;
                }
                return null;
              })
              .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id))
          : [];
        const consequenceIdsSet = new Set<number>(consequenceIds);
        if (typeof consequenceId === 'number') {
          consequenceIdsSet.add(consequenceId);
        }
        const normalizedConsequenceIds = Array.from(consequenceIdsSet.values());
        const actionItem: ActionType = {
          ...(actionItemPayload as ActionType),
          consequences: normalizedConsequenceIds as any,
        };
        if (state.deviation) {
          const projectId = state.deviation.project;
          if (typeof projectId === 'number') {
            const existingProjectActions = state.projectActionsByProject[projectId] ?? [];
            const deduped = existingProjectActions.filter((item) => item.id !== actionItem.id);
            const nextProjectActions = [...deduped, actionItem].sort((a: ActionType, b: ActionType) => {
              const orderA = a.project_order ?? Number.MAX_SAFE_INTEGER;
              const orderB = b.project_order ?? Number.MAX_SAFE_INTEGER;
              if (orderA !== orderB) {
                return orderA - orderB;
              }
              const idA = typeof a.id === 'number' ? a.id : Number.MAX_SAFE_INTEGER;
              const idB = typeof b.id === 'number' ? b.id : Number.MAX_SAFE_INTEGER;
              return idA - idB;
            });
            state.projectActionsByProject[projectId] = nextProjectActions;
            state.projectActionsLoaded[projectId] = true;
          }
          const existingActions = state.deviation.actions ?? [];
          const hasAction = existingActions.some((item) => item.id === actionItem.id);
          state.deviation.actions = hasAction
            ? existingActions.map((item) => (item.id === actionItem.id ? { ...item, ...actionItem } : item))
            : [...existingActions, actionItem];
          if (state.deviation.consequences) {
            state.deviation.consequences = state.deviation.consequences.map((consequence) => {
              if (!consequence.id) return consequence;
              const shouldHaveAction = normalizedConsequenceIds.includes(consequence.id);
              const existing = consequence.actions ?? [];
              const exists = existing.some((item) => item.id === actionItem.id);
              if (shouldHaveAction) {
                const nextActions = exists
                  ? existing.map((item) => (item.id === actionItem.id ? { ...item, ...actionItem } : item))
                  : [...existing, actionItem];
                return { ...consequence, actions: nextActions };
              }
              if (!exists) {
                return consequence;
              }
              return {
                ...consequence,
                actions: existing.filter((item) => item.id !== actionItem.id),
              };
            });
          }
          if (state.deviation.causes) {
            state.deviation.causes = state.deviation.causes.map((cause) => {
              if (!cause.consequences) return cause;
              const updatedConsequences = cause.consequences.map((consequence) => {
                if (!consequence.id) return consequence;
                const shouldHaveAction = normalizedConsequenceIds.includes(consequence.id);
                const existing = consequence.actions ?? [];
                const exists = existing.some((item) => item.id === actionItem.id);
                if (shouldHaveAction) {
                  const nextActions = exists
                    ? existing.map((item) => (item.id === actionItem.id ? { ...item, ...actionItem } : item))
                    : [...existing, actionItem];
                  return { ...consequence, actions: nextActions };
                }
                if (!exists) {
                  return consequence;
                }
                return {
                  ...consequence,
                  actions: existing.filter((item) => item.id !== actionItem.id),
                };
              });
              return { ...cause, consequences: updatedConsequences };
            });
          }
        }
      })
      .addCase(assignActionToConsequence.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCause.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteCause.fulfilled, (state, action) => {
        state.loading = false;
        if (state.deviation?.causes) {
          state.deviation.causes = state.deviation.causes.filter((cause) => cause.id !== action.payload);
        }
      })
      .addCase(deleteCause.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteConsequence.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteConsequence.fulfilled, (state, action) => {
        state.loading = false;
        if (state.deviation?.consequences) {
          state.deviation.consequences = state.deviation.consequences.filter(
            (consequence) => consequence.id !== action.payload
          );
        }
        if (state.deviation?.causes) {
          state.deviation.causes = state.deviation.causes.map((cause) => ({
            ...cause,
            consequences: (cause.consequences ?? []).filter((consequence) => consequence.id !== action.payload),
          }));
        }
      })
      .addCase(deleteConsequence.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteSafeguard.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSafeguard.fulfilled, (state, action) => {
        state.loading = false;
        if (state.deviation?.safeguards) {
          state.deviation.safeguards = state.deviation.safeguards.filter(
            (safeguard) => safeguard.id !== action.payload
          );
        }
        if (state.deviation?.consequences) {
          state.deviation.consequences = state.deviation.consequences.map((consequence) => ({
            ...consequence,
            safeguards: (consequence.safeguards ?? []).filter((safeguard) => safeguard.id !== action.payload),
          }));
        }
        if (state.deviation?.causes) {
          state.deviation.causes = state.deviation.causes.map((cause) => ({
            ...cause,
            consequences: (cause.consequences ?? []).map((consequence) => ({
              ...consequence,
              safeguards: (consequence.safeguards ?? []).filter((safeguard) => safeguard.id !== action.payload),
            })),
          }));
        }
      })
      .addCase(deleteSafeguard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAction.fulfilled, (state, action) => {
        state.loading = false;
        const payloadData = action.payload ?? {};
        const metaArgs = action.meta?.arg ?? {};
        const actionId = payloadData.actionId ?? metaArgs.actionId;
        const consequenceId =
          payloadData.consequenceId ?? metaArgs.consequenceId ?? null;
        if (typeof actionId !== 'number') {
          return;
        }

        const associatedConsequenceIds = new Set<number>();
        (state.deviation?.consequences ?? []).forEach((consequence) => {
          const consequenceIdValue = consequence?.id;
          if (typeof consequenceIdValue !== 'number') return;
          (consequence.actions ?? []).forEach((actionItem) => {
            if (actionItem?.id === actionId) {
              associatedConsequenceIds.add(consequenceIdValue);
            }
          });
        });

        const remainingConsequenceIds =
          typeof consequenceId === 'number'
            ? Array.from(associatedConsequenceIds).filter((id) => id !== consequenceId)
            : [];
        const actionStillExists =
          typeof consequenceId === 'number' ? remainingConsequenceIds.length > 0 : false;

        if (state.deviation?.actions) {
          if (actionStillExists) {
            state.deviation.actions = state.deviation.actions.map((actionItem) =>
              actionItem.id === actionId
                ? { ...actionItem, consequences: remainingConsequenceIds }
                : actionItem
            );
          } else {
            state.deviation.actions = state.deviation.actions.filter(
              (actionItem) => actionItem.id !== actionId
            );
          }
        }

        const projectId = state.deviation?.project;
        if (typeof projectId === 'number') {
          const existingProjectActions = state.projectActionsByProject[projectId] ?? [];
          if (actionStillExists) {
            state.projectActionsByProject[projectId] = existingProjectActions.map((actionItem) =>
              actionItem.id === actionId
                ? { ...actionItem, consequences: remainingConsequenceIds }
                : actionItem
            );
          } else {
            state.projectActionsByProject[projectId] = existingProjectActions.filter(
              (actionItem) => actionItem.id !== actionId
            );
          }
          state.projectActionsLoaded[projectId] = true;
        }

        const updateConsequenceActions = (
          actions: (ActionType & { [key: string]: any })[] | undefined,
          currentConsequenceId?: number | null
        ): typeof actions => {
          if (!Array.isArray(actions)) return actions;
          const result: typeof actions = [] as typeof actions;
          actions.forEach((actionItem) => {
            if (actionItem.id !== actionId) {
              result.push(actionItem);
              return;
            }
            if (!actionStillExists) {
              return;
            }
            if (typeof consequenceId === 'number' && currentConsequenceId === consequenceId) {
              return;
            }
            result.push({
              ...actionItem,
              consequences: remainingConsequenceIds,
            } as typeof actionItem);
          });
          return result;
        };

        if (state.deviation?.consequences) {
          state.deviation.consequences = state.deviation.consequences.map((consequence) => ({
            ...consequence,
            actions: updateConsequenceActions(consequence.actions ?? undefined, consequence.id ?? null),
          }));
        }
        if (state.deviation?.causes) {
          state.deviation.causes = state.deviation.causes.map((cause) => ({
            ...cause,
            consequences: (cause.consequences ?? []).map((consequence) => ({
              ...consequence,
              actions: updateConsequenceActions(consequence.actions ?? undefined, consequence.id ?? null),
            })),
          }));
        }
      })
      .addCase(deleteAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCause.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCause.fulfilled, (state, action) => {
        state.loading = false;
        if (state.deviation?.causes) {
          state.deviation.causes = state.deviation.causes.map((cause) =>
            cause.id === action.payload.id ? { ...cause, ...action.payload } : cause
          );
        }
      })
      .addCase(updateCause.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateConsequence.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateConsequence.fulfilled, (state, action) => {
        state.loading = false;
        const payloadData = action.payload ?? {};
        const requestData = action.meta?.arg ?? {};
        const updatedConsequence = {
          ...requestData,
          ...payloadData,
        };
        const targetId = updatedConsequence?.id;
        if (targetId === null || targetId === undefined) {
          return;
        }
        if (state.deviation?.consequences) {
          state.deviation.consequences = state.deviation.consequences.map((consequence) =>
            consequence.id === targetId ? { ...consequence, ...updatedConsequence } : consequence
          );
        }
        if (state.deviation?.causes) {
          state.deviation.causes = state.deviation.causes.map((cause) => {
            if (!cause.consequences) return cause;
            return {
              ...cause,
              consequences: cause.consequences.map((consequence) =>
                consequence.id === targetId ? { ...consequence, ...updatedConsequence } : consequence
              ),
            };
          });
        }
      })
      .addCase(updateConsequence.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateSafeguard.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSafeguard.fulfilled, (state, action) => {
        state.loading = false;
        if (state.deviation?.safeguards) {
          state.deviation.safeguards = state.deviation.safeguards.map((safeguard) =>
            safeguard.id === action.payload.id ? { ...safeguard, ...action.payload } : safeguard
          );
        }
        if (state.deviation?.consequences) {
          state.deviation.consequences = state.deviation.consequences.map((consequence) => {
            if (!consequence.safeguards) return consequence;
            return {
              ...consequence,
              safeguards: consequence.safeguards.map((safeguard) =>
                safeguard.id === action.payload.id ? { ...safeguard, ...action.payload } : safeguard
              ),
            };
          });
        }
        if (state.deviation?.causes) {
          state.deviation.causes = state.deviation.causes.map((cause) => {
            if (!cause.consequences) return cause;
            return {
              ...cause,
              consequences: cause.consequences.map((consequence) => {
                if (!consequence.safeguards) return consequence;
                return {
                  ...consequence,
                  safeguards: consequence.safeguards.map((safeguard) =>
                    safeguard.id === action.payload.id ? { ...safeguard, ...action.payload } : safeguard
                  ),
                };
              }),
            };
          });
        }
      })
      .addCase(updateSafeguard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAction.fulfilled, (state, action) => {
        state.loading = false;
        const projectId = state.deviation?.project;
        if (typeof projectId === 'number') {
          const existingProjectActions = state.projectActionsByProject[projectId] ?? [];
          const nextProjectActions = existingProjectActions
            .map((item) => (item.id === action.payload.id ? { ...item, ...action.payload } : item))
            .sort((a: ActionType, b: ActionType) => {
              const orderA = a.project_order ?? Number.MAX_SAFE_INTEGER;
              const orderB = b.project_order ?? Number.MAX_SAFE_INTEGER;
              if (orderA !== orderB) {
                return orderA - orderB;
              }
              const idA = typeof a.id === 'number' ? a.id : Number.MAX_SAFE_INTEGER;
              const idB = typeof b.id === 'number' ? b.id : Number.MAX_SAFE_INTEGER;
              return idA - idB;
            });
          state.projectActionsByProject[projectId] = nextProjectActions;
          state.projectActionsLoaded[projectId] = true;
        }
        if (state.deviation?.actions) {
          state.deviation.actions = state.deviation.actions.map((actionItem) =>
            actionItem.id === action.payload.id ? { ...actionItem, ...action.payload } : actionItem
          );
        }
        if (state.deviation?.consequences) {
          state.deviation.consequences = state.deviation.consequences.map((consequence) => {
            if (!consequence.actions) return consequence;
            return {
              ...consequence,
              actions: consequence.actions.map((actionItem) =>
                actionItem.id === action.payload.id ? { ...actionItem, ...action.payload } : actionItem
              ),
            };
          });
        }
        if (state.deviation?.causes) {
          state.deviation.causes = state.deviation.causes.map((cause) => {
            if (!cause.consequences) return cause;
            return {
              ...cause,
              consequences: cause.consequences.map((consequence) => {
                if (!consequence.actions) return consequence;
                return {
                  ...consequence,
                  actions: consequence.actions.map((actionItem) =>
                    actionItem.id === action.payload.id ? { ...actionItem, ...action.payload } : actionItem
                  ),
                };
              }),
            };
          });
        }
      })
      .addCase(updateAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(moveDeviationUp.pending, (state) => {
        state.loading = true;
      })
      .addCase(moveDeviationUp.fulfilled, (state, action) => {
        state.loading = false;
        // Lógica para actualizar el orden en el estado si es necesario
      })
      .addCase(moveDeviationUp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(moveDeviationDown.pending, (state) => {
        state.loading = true;
      })
      .addCase(moveDeviationDown.fulfilled, (state, action) => {
        state.loading = false;
        // Lógica para actualizar el orden en el estado si es necesario
      })
      .addCase(moveDeviationDown.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setDeviation, clearDeviation } = deviationSlice.actions;

export default deviationSlice.reducer;
